import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import { X, Loader2, Table2 } from 'lucide-react';
import { securityCommandControlLogApi } from '../services/api';
import type { ScclImportBatchDetail, ScclImportBatchSummary, ScclImportHistoryPage } from '../types';
import { SecurityCommandControlLogImportRowsPreviewTable } from './SecurityCommandControlLogImportRowsPreview';
import { SingleDayCalendarPicker } from './SingleDayCalendarPicker';

export interface SecurityCommandControlLogImportHistoryModalProps {
  open: boolean;
  onClose: () => void;
  entryId: number;
  entryLabel?: string;
}

export const SecurityCommandControlLogImportHistoryModal: React.FC<SecurityCommandControlLogImportHistoryModalProps> = ({
  open,
  onClose,
  entryId,
  entryLabel,
}) => {
  const [data, setData] = useState<ScclImportHistoryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterError, setFilterError] = useState<string | null>(null);

  const [viewingBatchId, setViewingBatchId] = useState<number | null>(null);
  const [viewingDetail, setViewingDetail] = useState<ScclImportBatchDetail | null>(null);
  const [viewingLoading, setViewingLoading] = useState(false);
  const [viewingError, setViewingError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setFromDate('');
    setToDate('');
    setFilterError(null);
    setViewingBatchId(null);
    setViewingDetail(null);
    setViewingError(null);
  }, [open, entryId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void securityCommandControlLogApi
      .importHistory(entryId, {
        page,
        pageSize,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Could not load import history.');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, entryId, page, pageSize, fromDate, toDate]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [data]);

  const handleDownloadOriginal = useCallback(async (h: ScclImportBatchSummary) => {
    if (!h.fileDownloadUrl) return;
    try {
      const blob = await securityCommandControlLogApi.importDownloadUploadedFile(h.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = h.fileName?.replace(/[\\/]/g, '_') || `sccl-import-batch-${h.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, []);

  const openViewBatchRows = useCallback(async (batchId: number) => {
    setViewingBatchId(batchId);
    setViewingDetail(null);
    setViewingError(null);
    setViewingLoading(true);
    try {
      const d = await securityCommandControlLogApi.importBatchDetail(batchId);
      setViewingDetail(d);
    } catch {
      setViewingError('Could not load import details.');
    } finally {
      setViewingLoading(false);
    }
  }, []);

  const closeViewBatchRows = useCallback(() => {
    setViewingBatchId(null);
    setViewingDetail(null);
    setViewingError(null);
    setViewingLoading(false);
  }, []);

  const handleSearch = () => {
    setFilterError(null);
    if (fromDate && toDate && fromDate > toDate) {
      setFilterError('From date must be on or before To date.');
      return;
    }
    setPage(1);
  };

  const canResetFilters = Boolean(fromDate || toDate);
  const handleResetFilters = () => {
    setFilterError(null);
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Import history</h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              Entry #{entryId}{entryLabel ? ` · ${entryLabel}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-col gap-0.5 text-xs font-medium text-gray-600">
              <span>From date</span>
              <SingleDayCalendarPicker value={fromDate} onChange={setFromDate} weekStartsOn={1} max={toDate || undefined} />
            </div>
            <div className="flex flex-col gap-0.5 text-xs font-medium text-gray-600">
              <span>To date</span>
              <SingleDayCalendarPicker value={toDate} onChange={setToDate} weekStartsOn={1} min={fromDate || undefined} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={!canResetFilters || loading}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
              >
                Reset
              </button>
            </div>
            {filterError ? <p className="w-full text-xs text-red-600">{filterError}</p> : null}
          </div>

          {loadError ? (
            <p className="text-sm text-red-700">{loadError}</p>
          ) : loading && !data ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : data && data.items.length === 0 ? (
            <p className="text-sm text-gray-500">{fromDate || toDate ? 'No imports match these dates.' : 'No imports yet for this entry.'}</p>
          ) : data ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full text-left text-sm text-gray-900">
                  <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        ID
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Status
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Created (UTC)
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Rows
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Valid / Invalid
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Imported / Failed
                      </th>
                      <th scope="col" className="min-w-[8rem] px-3 py-2.5">
                        File
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((h) => (
                      <tr key={h.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/80">
                        <td className="whitespace-nowrap px-3 py-2.5 font-medium">#{h.id}</td>
                        <td className="whitespace-nowrap px-3 py-2.5">{h.status}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                          {formatCompanyDateTime(h.createdAtUtc)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{h.totalRows}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-700">
                          {h.validRowCount} / {h.invalidRowCount}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-700">
                          {h.status === 'Committed' ? `${h.importedCount} / ${h.failedCount}` : '—'}
                        </td>
                        <td className="max-w-[14rem] truncate px-3 py-2.5 text-gray-600" title={h.fileName ?? undefined}>
                          {h.fileName ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                              onClick={() => void openViewBatchRows(h.id)}
                            >
                              <Table2 className="h-3.5 w-3.5 shrink-0" />
                              Preview
                            </button>
                            {h.fileDownloadUrl ? (
                              <button
                                type="button"
                                className="text-blue-600 hover:underline"
                                onClick={() => void handleDownloadOriginal(h)}
                              >
                                Download
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  className={`rounded border px-3 py-1.5 text-sm ${
                    page <= 1 ? 'pointer-events-none border-gray-200 text-gray-400' : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className={`rounded border px-3 py-1.5 text-sm ${
                    page >= totalPages
                      ? 'pointer-events-none border-gray-200 text-gray-400'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
                <span className="ml-auto text-xs text-gray-500">
                  Page {data.page} of {totalPages} · {data.totalCount} batch{data.totalCount === 1 ? '' : 'es'}
                </span>
              </div>
            </>
          ) : null}
        </div>

        {viewingBatchId != null && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
            <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">Batch #{viewingBatchId} row preview</h3>
                  <p className="mt-0.5 text-xs text-gray-500">Validation and commit results for each row.</p>
                </div>
                <button type="button" onClick={closeViewBatchRows} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {viewingLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                  </div>
                ) : viewingError ? (
                  <p className="text-sm text-red-700">{viewingError}</p>
                ) : viewingDetail ? (
                  <SecurityCommandControlLogImportRowsPreviewTable rows={viewingDetail.rows} />
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

